import { create } from 'zustand';

export type CreditsModalTourAccessedFrom =
  | 'checklist'
  | 'cart_home'
  | 'cart_shop'
  | 'cart_order'
  | 'cart_how_do_credits_work';

/**
 * Arguments for displaying a rating modal
 */
interface RatingModalArgs {
  /** The order ID that the rating modal should display ratings for */
  orderId: number;
  /** Number of recent orders to include in rating data (defaults to 10) */
  recentOrderCount?: number;
}

/**
 * Arguments for displaying a reactivation promo modal
 */
interface ReactivationPromoModalArgs {
  /** The promo code to use for reactivation (optional) */
  promoCode?: string;
}

/**
 * Arguments for displaying a reschedule modal
 */
interface RescheduleModalArgs {
  /** The order to reschedule */
  order: {
    origin: number | undefined; // Technically OriginEnum | undefined, but we don't want to import the type here
    id: number;
    cutoff_datetime: string;
    inventory_date: string | null;
    release_date?: string | null;
    preferred_date: string;
    possible_delivery_dates: {
      delivery_date: string;
      cutoff_datetime: string;
    }[];
    sequence: number;
  };
  /** Test ID for testing purposes (optional) */
  testid?: string;
  /** Whether the modal was opened from cart context (defaults to false) */
  openedFromCart?: boolean;
}

/**
 * Central state for all modal types in the application
 * This allows for centralized modal management with priority ordering
 */
interface ModalState {
  /** Reactivation promo modal for reactivating canceled subscriptions - highest priority */
  reactivationPromoModal: ReactivationPromoModalArgs | null;
  /** Rating modal for order feedback - shown when user clicks "Rate" button */
  ratingModal: RatingModalArgs | null;
  /** Reschedule modal for rescheduling order delivery - lowest priority among active modals */
  rescheduleModal: RescheduleModalArgs | null;
  /** Credits modal tour visibility state */
  creditsModalTourOpen: boolean;
  /** Where the credits modal tour was opened from */
  creditsModalTourAccessedFrom: CreditsModalTourAccessedFrom | null;
  /** Bookmark modal tour visibility state */
  bookmarkModalTourOpen: boolean;
  /** Checklist completion toast visibility state */
  checklistCompletionToastVisible: boolean;
  // Add other modal types here as they're needed
  // exampleModal: ExampleModalArgs | null;
}

/**
 * Zustand store interface for modal management
 * Provides centralized modal state with priority-based rendering
 */
interface ModalStore extends ModalState {
  /** Set the reactivation promo modal arguments or null to close it */
  setReactivationPromoModal: (args: ReactivationPromoModalArgs | null) => void;
  /** Set the rating modal arguments or null to close it */
  setRatingModal: (args: RatingModalArgs | null) => void;
  /** Set the reschedule modal arguments or null to close it */
  setRescheduleModal: (args: RescheduleModalArgs | null) => void;
  /** Set credits modal tour open state and the surface it was accessed from */
  setCreditsModalTourOpen: (
    open: boolean,
    accessedFrom?: CreditsModalTourAccessedFrom
  ) => void;
  /** Set bookmark modal tour open state */
  setBookmarkModalTourOpen: (open: boolean) => void;
  /** Set checklist completion toast visibility state */
  setChecklistCompletionToastVisible: (visible: boolean) => void;
  /** Clear all active modals */
  clearAllModals: () => void;
  /** Get the currently active modal based on priority order */
  getCurrentModal: () =>
    | { type: 'reactivationPromo'; args: ReactivationPromoModalArgs }
    | { type: 'rating'; args: RatingModalArgs }
    | { type: 'reschedule'; args: RescheduleModalArgs }
    | null;
  /** Reset the store to initial state (used for testing) */
  resetStore: () => void;
}

const initialState: ModalState = {
  reactivationPromoModal: null,
  ratingModal: null,
  rescheduleModal: null,
  creditsModalTourOpen: false,
  creditsModalTourAccessedFrom: null,
  bookmarkModalTourOpen: false,
  checklistCompletionToastVisible: false,
};

/**
 * Zustand store for managing application-wide modal state
 *
 * This store implements a priority-based modal system where only one modal
 * can be displayed at a time. The getCurrentModal() method determines which
 * modal should be shown based on a predefined priority order.
 *
 * Priority order (highest to lowest):
 * 1. Reactivation promo modal - for reactivating canceled subscriptions
 * 2. Rating modal - for order feedback
 * 3. Reschedule modal - for rescheduling order delivery
 *
 * @example
 * ```tsx
 * const { setRatingModal, setReactivationPromoModal, setRescheduleModal, getCurrentModal } = useModalStore();
 *
 * // Show reactivation promo modal
 * setReactivationPromoModal({ promoCode: 'SAVE20' });
 *
 * // Show rating modal
 * setRatingModal({ orderId: 123, recentOrderCount: 10 });
 *
 * // Show reschedule modal
 * setRescheduleModal({ order: someOrder, testid: 'test-reschedule', openedFromCart: false });
 *
 * // Check what modal should be displayed
 * const currentModal = getCurrentModal();
 * if (currentModal?.type === 'reactivationPromo') {
 *   return <ReactivationPromoModal {...currentModal.args} />;
 * } else if (currentModal?.type === 'rating') {
 *   return <RatingModal {...currentModal.args} />;
 * } else if (currentModal?.type === 'reschedule') {
 *   return <RescheduleModal {...currentModal.args} />;
 * }
 * ```
 */
export const useModalStore = create<ModalStore>((set, get) => ({
  ...initialState,

  setReactivationPromoModal: (args: ReactivationPromoModalArgs | null) =>
    set({ reactivationPromoModal: args }),

  setRatingModal: (args: RatingModalArgs | null) => set({ ratingModal: args }),

  setRescheduleModal: (args: RescheduleModalArgs | null) =>
    set({ rescheduleModal: args }),

  setCreditsModalTourOpen: (open, accessedFrom) =>
    set({
      creditsModalTourOpen: open,
      creditsModalTourAccessedFrom: open ? accessedFrom ?? null : null,
    }),

  setBookmarkModalTourOpen: (open: boolean) =>
    set({ bookmarkModalTourOpen: open }),

  setChecklistCompletionToastVisible: (visible: boolean) =>
    set({ checklistCompletionToastVisible: visible }),

  clearAllModals: () => set(initialState),

  getCurrentModal: () => {
    const state = get();

    // Priority order: reactivation promo modal first, then rating modal, then reschedule modal
    if (state.reactivationPromoModal) {
      return {
        type: 'reactivationPromo' as const,
        args: state.reactivationPromoModal,
      };
    }

    if (state.ratingModal) {
      return { type: 'rating' as const, args: state.ratingModal };
    }

    if (state.rescheduleModal) {
      return { type: 'reschedule' as const, args: state.rescheduleModal };
    }

    return null;
  },

  resetStore: () => set(initialState),
}));
